import { emptySmartLinksApi as api } from "./emptySmartLinksApi";
import { apiBaseUrl } from "../consts/api-url.const";
import { showSpotifyReconnect } from "../../store/popupSlice";
import toast from "react-toastify";

// TypeScript types for Smart Links API
export type PlatformVisibilityEntry =
  | boolean
  | {
      url?: string;
      iconUrl?: string;
      iconKey?: string;
      visited?: boolean;
      visible?: boolean;
    };

export type SmartLink = {
  _id: string;
  userId: string;
  campaignId?: string;
  campaignName?: string;
  spotifyUrl: string;
  smartLinkUrl: string;
  smartLinkCode?: string;
  linkPage?: string;
  linkOptional?: string;
  artistName?: string;
  songTitle?: string;
  platformUrls?: Record<string, string>;
  platformVisibility?: Record<string, PlatformVisibilityEntry>;
  platformVisibilityLegacy?: Record<string, boolean>;
  platformIconKeys?: Record<string, string>;
  customPlatforms?: Array<{
    id: string;
    name: string;
    iconUrl?: string;
    iconKey?: string;
    url: string;
    visible: boolean;
  }>;
  platform: string;
  promoteType?: "track" | "playlist";
  status: "active" | "inactive" | "archived";
  createdAt: string;
  updatedAt: string;
  artworkUrl?: string;
  facebookPixelId?: string;
  facebookCapiToken?: string;
  genres?: string[];
  analytics?: {
    totalVisits?: number;
    totalClicks?: number;
    visitsByCountry?: Record<string, number> | any;
    clicksByCountry?: Record<string, number> | any;
    clicksByPlatform?: Record<string, number> | any;
    visitsByDate?: Array<{ date: string; count: number }> | any;
    clicksByDate?: Array<{ date: string; count: number }> | any;
  };
  metadata?: any;
};

type GetSmartLinksResponse = {
  smartLinks: SmartLink[];
  total: number;
  page?: number;
  limit?: number;
  hasMore?: boolean;
};

type GetSmartLinkByIdResponse = {
  smartLink: SmartLink;
};

type GetSmartLinkMetaAnalyticsResponse =
  | {
      available: false;
      reason: "NO_CAMPAIGN_LINKED";
    }
  | {
      available: boolean;
      rangeDays: number;
      meta: any;
      normalized: {
        impressions: number;
        clicks: number;
        spend: number;
        ctr: number;
        cpc: number;
        cpm: number;
        reach: number;
        frequency: number;
      } | null;
      fetchedAt: string;
    };

type GetSmartLinkAnalyticsResponse = {
  totalVisits: number;
  totalClicks: number;
  visitsByCountry: Record<string, number>;
  clicksByCountry: Record<string, number>;
  clicksByPlatform: Record<string, number>;
  timeSeriesData: Array<{
    date: string;
    visits: number;
    clicks: number;
  }>;
  rangeDays: number;
};

type GetSmartLinksQueryParams = {
  userId?: string;
  status?: "active" | "inactive" | "archived";
  promoteType?: "track" | "playlist";
  page?: number;
  limit?: number;
};

type CreateSmartLinkRequest =
  | {
      spotifyUrl: string;
      artistName?: string;
      songTitle?: string;
      platformUrls?: Record<string, string>;
      platformVisibility?: Record<string, PlatformVisibilityEntry>;
      platformIcons?: Record<string, string>;
      customPlatforms?: Array<{
        id: string;
        name: string;
        iconUrl?: string;
        url: string;
        visible: boolean;
      }>;
      artworkUrl?: string | null;
      genres?: string[];
      platform?: string;
      promoteType?: "track" | "playlist";
      campaignId?: string;
      facebookPixelId?: string;
      facebookCapiToken?: string;
      linkPage?: string;
      linkOptional?: string;
    }
  | FormData;

type CreateSmartLinkResponse = {
  msg: string;
  smartLink: SmartLink;
};

type UpdateSmartLinkBody =
  | {
      status?: "active" | "inactive" | "archived";
      spotifyUrl?: string;
      artistName?: string;
      songTitle?: string;
      platformUrls?: Record<string, string>;
      platformVisibility?: Record<string, PlatformVisibilityEntry>;
      platformIcons?: Record<string, string>;
      customPlatforms?: Array<{
        id: string;
        name: string;
        iconUrl?: string;
        iconKey?: string;
        url: string;
        visible: boolean;
      }>;
      artworkUrl?: string | null;
      genres?: string[];
      promoteType?: "track" | "playlist";
      facebookPixelId?: string | null;
      facebookCapiToken?: string | null;
      linkPage?: string | null;
      linkOptional?: string | null;
    }
  | FormData;

type UpdateSmartLinkRequest = { id: string; body: UpdateSmartLinkBody };

type UpdateSmartLinkResponse = {
  msg: string;
  smartLink: SmartLink;
};

type DeleteSmartLinkResponse = {
  msg: string;
  smartLink?: SmartLink;
};

export type SmartLinkIcon = {
  _id: string;
  userId: string;
  name: string;
  s3Key: string;
  fileName: string;
  contentType: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  url: string;
};

type GetSmartLinkIconsResponse = {
  icons: SmartLinkIcon[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

// Inject endpoints
const injectedRtkApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Validate custom path for uniqueness
    validateSmartLinkPath: builder.mutation<
      { alreadyExists: boolean },
      { linkPage: string; linkOptional?: string; excludeId?: string }
    >({
      queryFn: async (body) => {
        const token = localStorage.getItem("token");

        const response = await fetch(`${apiBaseUrl}/api/smartlink/validate-path`, {
          method: "POST",
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorData: any = {};
          try {
            errorData = errorText ? JSON.parse(errorText) : {};
          } catch {
            errorData = {};
          }
          return {
            error: { status: response.status, data: errorData } as any,
          };
        }

        const data = await response.json() as { alreadyExists: boolean };
        return { data };
      },
    }),

    // GET all smart links for a user
    getSmartLinks: builder.query<GetSmartLinksResponse, GetSmartLinksQueryParams | void>({
      query: (params) => ({
        url: `/api/smartlink`,
        params: params || {},
      }),
      providesTags: ['SmartLinks'],
    }),

    // GET single smart link by ID
    getSmartLinkById: builder.query<GetSmartLinkByIdResponse, string>({
      query: (id) => ({
        url: `/api/smartlink/by-id/${id}`,
      }),
      providesTags: ['SmartLink'],
    }),

    getSmartLinkMetaAnalytics: builder.query<
      GetSmartLinkMetaAnalyticsResponse,
      { id: string; range?: "7" | "14" | "30" | "90" }
    >({
      query: ({ id, range = "7" }) => ({
        url: `/api/smartlink/${id}/meta-analytics`,
        params: { range },
      }),
    }),

    getSmartLinkAnalytics: builder.query<
      GetSmartLinkAnalyticsResponse,
      { id: string; range?: "7" | "14" | "30" | "90" }
    >({
      query: ({ id, range = "7" }) => ({
        url: `/api/smartlink/${id}/analytics`,
        params: { range },
      }),
    }),

    // GET smart links by campaign ID (public endpoint)
    getSmartLinksByCampaign: builder.query<GetSmartLinksResponse, string>({
      query: (campaignId) => ({
        url: `/api/smartlink/by-campaign/${campaignId}`,
      }),
      providesTags: ['SmartLinks'],
    }),

    // GET smart link by URL (public endpoint)
    getSmartLinkByUrl: builder.query<GetSmartLinkByIdResponse, string>({
      query: (url) => ({
        url: `/api/smartlink/by-url/${url}`,
      }),
      providesTags: ['SmartLink'],
    }),

    // CREATE smart link
    createSmartLink: builder.mutation<CreateSmartLinkResponse, CreateSmartLinkRequest>({
      queryFn: async (body, api, extraOptions) => {
        // Use custom fetch for FormData to properly handle multipart/form-data
        const token = localStorage.getItem("token");
        const metaAdsPublicToken = localStorage.getItem("metaAdsPublicToken");

        const headers: HeadersInit = {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(metaAdsPublicToken && { "x-auth-token": metaAdsPublicToken }),
        };

        // Don't set Content-Type for FormData - let browser set it with boundary.
        // Some environments can make `instanceof FormData` unreliable, so we use a fallback.
        const isFormData =
          (typeof FormData !== "undefined" && body instanceof FormData) ||
          (!!body &&
            typeof (body as any).append === "function" &&
            typeof (body as any).get === "function");
        if (!isFormData) {
          (headers as any)["Content-Type"] = "application/json";
        }

        // Ensure `body` matches `fetch`'s `BodyInit` type.
        const fetchBody: BodyInit | undefined = isFormData
          ? (body as unknown as BodyInit)
          : JSON.stringify(body);

        const response = await fetch(`${apiBaseUrl}/api/smartlink`, {
          method: "POST",
          headers,
          body: fetchBody,
        });

        if (!response.ok) {
          // Match RTK Query's expected `{ error: ... }` shape.
          const errorText = await response.text().catch(() => "");
          let errorData: any = {};
          try {
            errorData = errorText ? JSON.parse(errorText) : {};
          } catch {
            errorData = {};
          }
          return {
            error: { status: response.status, data: errorData } as any,
          };
        }

        const responseText = await response.text().catch(() => "");
        let data: CreateSmartLinkResponse;
        try {
          if (!responseText) {
            return {
              error: {
                status: response.status,
                data: { msg: "Empty response from createSmartLink" },
              },
            } as any;
          }
          data = JSON.parse(responseText) as CreateSmartLinkResponse;
        } catch {
          return {
            error: {
              status: response.status,
              data: { msg: "Invalid JSON from createSmartLink" },
            },
          } as any;
        }

        // RTK Query `queryFn` must return `{ data: ... }` with non-undefined value.
        return { data };
      },
      invalidatesTags: ['SmartLinks'],
    }),

    // UPDATE smart link
    updateSmartLink: builder.mutation<UpdateSmartLinkResponse, UpdateSmartLinkRequest>({
      queryFn: async ({ id, body }, api, extraOptions) => {
        // Use custom fetch for FormData to properly handle multipart/form-data
        const token = localStorage.getItem("token");

        const headers: HeadersInit = {
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        // Don't set Content-Type for FormData - let browser set it with boundary.
        // Some environments can make `instanceof FormData` unreliable, so we use a fallback.
        const isFormData =
          (typeof FormData !== "undefined" && body instanceof FormData) ||
          (!!body &&
            typeof (body as any).append === "function" &&
            typeof (body as any).get === "function");
        if (!isFormData) {
          (headers as any)["Content-Type"] = "application/json";
        }

        // Ensure `body` matches `fetch`'s `BodyInit` type.
        const fetchBody: BodyInit | undefined = isFormData
          ? (body as unknown as BodyInit)
          : JSON.stringify(body);

        const response = await fetch(`${apiBaseUrl}/api/smartlink/${id}`, {
          method: "PUT",
          headers,
          body: fetchBody,
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorData: any = {};
          try {
            errorData = errorText ? JSON.parse(errorText) : {};
          } catch {
            errorData = {};
          }
          return {
            error: { status: response.status, data: errorData } as any,
          };
        }

        const responseText = await response.text().catch(() => "");
        let data: UpdateSmartLinkResponse;
        try {
          if (!responseText) {
            return {
              error: {
                status: response.status,
                data: { msg: "Empty response from updateSmartLink" },
              },
            } as any;
          }
          data = JSON.parse(responseText) as UpdateSmartLinkResponse;
        } catch {
          return {
            error: {
              status: response.status,
              data: { msg: "Invalid JSON from updateSmartLink" },
            },
          } as any;
        }

        return { data };
      },
      invalidatesTags: ['SmartLinks', 'SmartLink'],
    }),

    // DELETE smart link
    deleteSmartLink: builder.mutation<DeleteSmartLinkResponse, string>({
      query: (id) => ({
        url: `/api/smartlink/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ['SmartLinks'],
    }),

    // GET smart link icons
    getSmartLinkIcons: builder.query<GetSmartLinkIconsResponse, void>({
      query: () => ({
        url: `/api/smartlink/icons`,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export API and hooks
export { injectedRtkApi as smartLinksApi };

export const {
  useGetSmartLinksQuery,
  useGetSmartLinkByIdQuery,
  useGetSmartLinkMetaAnalyticsQuery,
  useGetSmartLinkAnalyticsQuery,
  useGetSmartLinksByCampaignQuery,
  useGetSmartLinkByUrlQuery,
  useGetSmartLinkIconsQuery,
  useCreateSmartLinkMutation,
  useUpdateSmartLinkMutation,
  useDeleteSmartLinkMutation,
  useValidateSmartLinkPathMutation,
} = injectedRtkApi;
